import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Trophy, MapPin, Clock, ArrowRight, Activity, Megaphone, ShieldCheck } from "lucide-react";
import { api } from "../../lib/api";

const PRIMARY = "#4F1B7C";
const SECONDARY = "#EC1F7C";

/**
 * PlayerProDashboard — espace dédié aux joueurs pro et staff joueurs.
 *
 * Affiche :
 *   - prochain match du club (depuis /api/sport/next-match)
 *   - planning des entraînements de l'équipe pro (Sénior 1, lecture seule)
 *   - lien rapide vers la console Speaker / Coach
 *
 * Pas de mutations : le joueur ne peut pas modifier le planning. Il pousse
 * vers la console coach pour tout ajustement.
 */
export default function PlayerProDashboard({ customer }) {
  const [nextMatch, setNextMatch] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get("/sport/next-match").then((r) => r.data?.match || null).catch(() => null),
      api
        .get("/sport/licensee/schedule", {
          headers: localStorage.getItem("msc_customer_token")
            ? { Authorization: `Bearer ${localStorage.getItem("msc_customer_token")}` }
            : undefined,
        })
        .then((r) => r.data?.sessions || [])
        .catch(() => []),
    ]).then(([m, s]) => {
      if (!alive) return;
      setNextMatch(m);
      setSessions(s);
      setLoading(false);
    });
    return () => { alive = false; };
  }, []);

  const fmtDate = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleDateString("fr-FR", { weekday: "long", day: "2-digit", month: "long" });
    } catch { return iso; }
  };
  const fmtTime = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }); }
    catch { return ""; }
  };

  const upcomingSessions = (sessions || [])
    .filter((s) => s.start_time && new Date(s.start_time).getTime() > Date.now() - 6 * 3600 * 1000)
    .sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""))
    .slice(0, 5);

  return (
    <div className="space-y-6" data-testid="player-pro-dashboard">
      {/* Welcome */}
      <div className="rounded-3xl p-6 sm:p-8 relative overflow-hidden"
        style={{ background: `linear-gradient(140deg, ${PRIMARY} 0%, #2c0e4a 60%, #160732 100%)` }}>
        <div className="absolute inset-0 opacity-20 pointer-events-none"
          style={{ backgroundImage: "radial-gradient(circle, white 1px, transparent 1px)", backgroundSize: "12px 12px" }}/>
        <div className="relative flex items-center gap-4 flex-wrap">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center"
            style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` }}>
            <Trophy size={28} className="text-white"/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-pink-300 font-black tracking-[0.3em] text-[10px] uppercase">Espace Joueur Pro</div>
            <div className="text-white font-blackhawk italic uppercase text-2xl sm:text-3xl leading-tight">
              Salle de réveil
            </div>
            <div className="text-white/65 text-sm mt-1">
              Bonjour {customer?.first_name || "Champion"}. Toutes les infos clé du club sont ici.
            </div>
          </div>
        </div>
      </div>

      {/* Next match */}
      <div className="rounded-3xl bg-white shadow-xl border border-slate-100 overflow-hidden" data-testid="player-next-match">
        <div className="px-5 py-3 flex items-center justify-between" style={{ background: `linear-gradient(135deg, ${PRIMARY}, #2c0e4a)` }}>
          <div>
            <div className="text-pink-300 font-black tracking-[0.3em] text-[10px] uppercase">À préparer</div>
            <div className="text-white font-blackhawk italic uppercase text-base sm:text-lg leading-tight">Prochain match</div>
          </div>
          <Calendar className="text-pink-300" size={18}/>
        </div>
        {loading ? (
          <div className="px-5 py-8 text-center text-slate-400 italic">Chargement…</div>
        ) : nextMatch ? (
          <div className="px-5 py-5 grid grid-cols-[1fr,auto,1fr] items-center gap-3">
            <div className="flex flex-col items-center text-center">
              <img src={nextMatch.home_logo} alt="" className="w-16 h-16 object-contain"/>
              <div className="text-slate-800 font-black uppercase text-xs mt-2 truncate w-full max-w-[120px]">{nextMatch.home_team}</div>
            </div>
            <div className="text-center">
              <div className="font-blackhawk italic text-4xl tracking-widest" style={{ color: SECONDARY }}>VS</div>
              <div className="text-slate-700 font-black text-sm mt-1">{nextMatch.date}</div>
              <div className="text-slate-500 text-xs uppercase font-bold tracking-widest">{nextMatch.time || "—"}</div>
              {nextMatch.venue && <div className="text-slate-400 text-[10px] italic mt-1 truncate max-w-[180px]">{nextMatch.venue}</div>}
            </div>
            <div className="flex flex-col items-center text-center">
              <img src={nextMatch.away_logo} alt="" className="w-16 h-16 object-contain"/>
              <div className="text-slate-800 font-black uppercase text-xs mt-2 truncate w-full max-w-[120px]">{nextMatch.away_team}</div>
            </div>
          </div>
        ) : (
          <div className="px-5 py-8 text-center text-slate-400 italic">Pas de prochain match programmé.</div>
        )}
      </div>

      {/* Training sessions (read-only) */}
      <div className="rounded-3xl bg-white shadow-xl border border-slate-100 overflow-hidden" data-testid="player-schedule">
        <div className="px-5 py-3 flex items-center justify-between border-b border-slate-100">
          <div>
            <div className="text-pink-600 font-black tracking-[0.3em] text-[10px] uppercase">Préparation</div>
            <div className="font-blackhawk italic uppercase text-base sm:text-lg leading-tight" style={{ color: PRIMARY }}>Planning équipe</div>
          </div>
          <Activity className="text-pink-500" size={18}/>
        </div>
        {loading ? (
          <div className="px-5 py-8 text-center text-slate-400 italic">Chargement…</div>
        ) : upcomingSessions.length ? (
          <ul className="divide-y divide-slate-100">
            {upcomingSessions.map((s) => (
              <li key={s.id} className="px-5 py-3 flex items-center gap-3 text-sm">
                <div className={`shrink-0 w-9 h-9 rounded-xl flex items-center justify-center ${s.kind === "match" ? "text-white" : "text-pink-600"}`}
                  style={{ background: s.kind === "match" ? `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` : "rgba(236,31,124,0.12)" }}>
                  {s.kind === "match" ? <Trophy size={16}/> : <Activity size={16}/>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-slate-800 truncate">
                    {s.kind === "match" ? `Match : ${s.opponent || "Adversaire"}` : (s.title || "Entraînement")}
                  </div>
                  <div className="text-slate-500 text-xs flex items-center gap-2 flex-wrap mt-0.5">
                    <span className="inline-flex items-center gap-1"><Clock size={11}/> {fmtDate(s.start_time)} · {fmtTime(s.start_time)}</span>
                    {s.location && <span className="inline-flex items-center gap-1"><MapPin size={11}/> {s.location}</span>}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="px-5 py-8 text-center text-slate-400 italic text-sm">Aucune séance à venir programmée.</div>
        )}
      </div>

      {/* Quick actions */}
      <div className="grid sm:grid-cols-3 gap-4" data-testid="player-quick-actions">
        <Link to="/sport/perfs-wall" target="_blank" rel="noopener noreferrer"
          className="rounded-2xl p-5 bg-gradient-to-br from-pink-500 to-pink-600 text-white shadow-lg hover:scale-[1.02] transition group">
          <Megaphone size={22} className="mb-2"/>
          <div className="font-blackhawk italic uppercase text-base">Perfs Wall</div>
          <div className="text-white/80 text-xs mt-1">Suivre le live diffusé en stade.</div>
        </Link>
        <Link to="/sport/effectif"
          className="rounded-2xl p-5 bg-white border border-slate-200 shadow-sm hover:shadow-md transition group">
          <ShieldCheck size={22} className="mb-2" style={{ color: PRIMARY }}/>
          <div className="font-blackhawk italic uppercase text-base" style={{ color: PRIMARY }}>L'effectif</div>
          <div className="text-slate-500 text-xs mt-1">Mes coéquipiers et staff.</div>
          <div className="text-pink-500 text-[11px] uppercase font-black tracking-widest mt-3 inline-flex items-center gap-1 group-hover:gap-2 transition-all">
            Voir <ArrowRight size={12}/>
          </div>
        </Link>
        <Link to="/sport/calendrier"
          className="rounded-2xl p-5 bg-white border border-slate-200 shadow-sm hover:shadow-md transition group">
          <Calendar size={22} className="mb-2" style={{ color: PRIMARY }}/>
          <div className="font-blackhawk italic uppercase text-base" style={{ color: PRIMARY }}>Saison</div>
          <div className="text-slate-500 text-xs mt-1">Calendrier et résultats.</div>
          <div className="text-pink-500 text-[11px] uppercase font-black tracking-widest mt-3 inline-flex items-center gap-1 group-hover:gap-2 transition-all">
            Voir <ArrowRight size={12}/>
          </div>
        </Link>
      </div>
    </div>
  );
}
